import React, { useContext, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FiBarChart2,
  FiChevronDown,
  FiCreditCard,
  FiDollarSign,
  FiGrid,
  FiLogOut,
  FiMapPin,
  FiRefreshCcw,
  FiSettings,
  FiShield,
  FiUploadCloud,
} from "react-icons/fi";
import { UserContext } from "../UserContext";
import slogo from "../assets/images/spare_logo_title.PNG";

const sections = [
  {
    label: "Overview",
    items: [
      { label: "Dashboard", path: "/dashboard", icon: FiGrid },
    ],
  },
  {
    label: "Network",
    items: [
      { label: "Manage Locations", path: "/view-locations", icon: FiMapPin },
      { label: "Fee Settings", path: "/fee-settings", icon: FiDollarSign },
      { label: "Sponsorship", path: "/sponsorship", icon: FiSettings },
        { label: "Add Bulk Locations", path: "/csv-upload", icon: FiUploadCloud },
        { label: "Add Single Location", path: "/manual-upload", icon: FiUploadCloud },
    ],
  },
  {
    label: "Licenses & Billing",
    items: [
      { label: "Manage Licenses", path: "/manage-licenses", icon: FiCreditCard },
      { label: "License Billing", path: "/billing", icon: FiDollarSign },
      { label: "Payouts", path: "/payouts", icon: FiSettings },
    ],
  },
  {
    label: "Transfers",
    items: [
      { label: "Transfer Locations", path: "/transfer", icon: FiRefreshCcw },
      { label: "Transfer Requests", path: "/transfer-locations", icon: FiRefreshCcw },
    ],
  },
  // {
  //   label: "Reporting",
  //   items: [
  //     { label: "Transfers Report", path: "/transfers-report", icon: FiBarChart2 },
  //     { label: "Marketplace Report", path: "/license-marketplace-report", icon: FiBarChart2 },
  //   ],
  // },
];

function getActiveSections(pathname) {
  const open = new Set();
  sections.forEach((section) => {
    if (section.items.some((item) => pathname === item.path || pathname.startsWith(`${item.path}/`))) {
      open.add(section.label);
    }
  });
  return open;
}

function Sidebar() {
  const { isLoggedIn, user, logout } = useContext(UserContext);
  const location = useLocation();
  const navigate = useNavigate();
  const { pathname } = location;

  const [expanded, setExpanded] = useState(() => getActiveSections(pathname));

  useEffect(() => {
    setExpanded((prev) => new Set([...prev, ...getActiveSections(pathname)]));
  }, [pathname]);

  if (!isLoggedIn) return null;

  const isItemActive = (path) => pathname === path || pathname.startsWith(`${path}/`);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const toggle = (label) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };

  return (
    <aside className="sidebar">
      <div className="sidebar-brand">
        <img alt="Spare" src={slogo} />
        <div className="sidebar-brand-copy">
          <span className="sidebar-brand-eyebrow">Spare Admin</span>
          <span className="sidebar-brand-title">ISO Portal</span>
        </div>
      </div>

      <div className="sidebar-account">
        <div className="sidebar-account__summary">
          <span className="sidebar-account__label">Signed in as</span>
          <strong>{user?.name || "Operator"}</strong>
        </div>
        <div className="sidebar-account__actions">
          <Link
            className={`sidebar-account__link${isItemActive("/account-settings") ? " active" : ""}`}
            to="/account-settings"
          >
            <span className="sidebar-link-icon"><FiSettings size={16} /></span>
            <span>Account Settings</span>
          </Link>
          <Link
            className={`sidebar-account__link${isItemActive("/change-password") ? " active" : ""}`}
            to="/change-password"
          >
            <span className="sidebar-link-icon"><FiShield size={16} /></span>
            <span>Change Password</span>
          </Link>
          <button className="sidebar-account__button" type="button" onClick={handleLogout}>
            <span className="sidebar-link-icon"><FiLogOut size={16} /></span>
            <span>Logout</span>
          </button>
        </div>
      </div>

      <nav className="sidebar-nav" aria-label="Primary portal navigation">
        {sections.map((section) => {
          const isOpen = expanded.has(section.label);
          const hasActive = section.items.some((item) => isItemActive(item.path));
          return (
            <section
              className={`sidebar-section${isOpen ? " is-open" : ""}${hasActive ? " has-active" : ""}`}
              key={section.label}
            >
              <button
                className="sidebar-section-toggle"
                type="button"
                onClick={() => toggle(section.label)}
                aria-expanded={isOpen}
              >
                <span className="sidebar-heading">{section.label}</span>
                <FiChevronDown className="sidebar-chevron" size={13} />
              </button>
              <ul className="sidebar-links">
                {section.items.map((item) => {
                  const Icon = item.icon;
                  const active = isItemActive(item.path);
                  return (
                    <li key={item.path}>
                      <Link to={item.path} className={active ? "active" : ""}>
                        <span className="sidebar-link-icon"><Icon size={16} /></span>
                        <span>{item.label}</span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </section>
          );
        })}
      </nav>
    </aside>
  );
}

export default Sidebar;
